// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Tooltip,
  Menu,
  MenuItem,
  Fab,
  Snackbar,
  SnackbarContent,
} from '@mui/material';
import { Grid } from '@mui/material';

import {
  PlayArrow as ContinueIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileCopy as DuplicateIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Schedule as ScheduleIcon,
  Message as MessageIcon,
  Description as DocumentIcon,
  Info as InfoIcon,
  DeleteSweep as ClearAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  getDraftSummaries,
  deleteDraft,
  duplicateDraft,
  updateDraftMetadata,
  clearAllDrafts,
  getDraftStats,
  DraftSummary,
} from '../../services/draftService';
import './DraftsPage.css';

// Interface for component state
interface DraftStatsData {
  totalDrafts: number;
  totalMessages: number;
  storageSize: number;
  oldestDraft?: string;
  newestDraft?: string;
}

const DraftsPage: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [drafts, setDrafts] = useState<DraftSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<DraftStatsData | null>(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clearAllDialogOpen, setClearAllDialogOpen] = useState(false);
  const [selectedDraft, setSelectedDraft] = useState<DraftSummary | null>(null);

  // Edit form state
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuDraftId, setMenuDraftId] = useState<string | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Load drafts and stats
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [draftSummaries, draftStats] = await Promise.all([
        getDraftSummaries(),
        getDraftStats(),
      ]);

      setDrafts(draftSummaries);
      setStats(draftStats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Handle continuing a draft
  const handleContinueDraft = (draftId: string) => {
    // Navigate to chat page with draft ID as query parameter
    window.location.replace(`/chat?draft=${draftId}`);
  };

  // Handle opening edit dialog
  const handleEditDraft = (draft: DraftSummary) => {
    setSelectedDraft(draft);
    setEditTitle(draft.title);
    setEditDescription(draft.description || '');
    setEditDialogOpen(true);
    handleMenuClose();
  };

  // Handle saving edited draft metadata
  const handleSaveEdit = async () => {
    if (!selectedDraft) return;

    try {
      await updateDraftMetadata(selectedDraft.id, editTitle, editDescription);
      await loadData(); // Reload data
      setEditDialogOpen(false);
      showSnackbar('Draft updated successfully', 'success');
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to update draft',
        'error'
      );
    }
  };

  // Handle delete confirmation
  const handleDeleteDraft = (draft: DraftSummary) => {
    setSelectedDraft(draft);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  // Handle confirming delete
  const handleConfirmDelete = async () => {
    if (!selectedDraft) return;

    try {
      await deleteDraft(selectedDraft.id);
      await loadData(); // Reload data
      setDeleteDialogOpen(false);
      showSnackbar('Draft deleted successfully', 'success');
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to delete draft',
        'error'
      );
    }
  };

  // Handle duplicate draft
  const handleDuplicateDraft = async (draftId: string) => {
    try {
      await duplicateDraft(draftId);
      await loadData(); // Reload data
      showSnackbar('Draft duplicated successfully', 'success');
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to duplicate draft',
        'error'
      );
    }
    handleMenuClose();
  };

  // Handle clear all drafts
  const handleClearAll = () => {
    setClearAllDialogOpen(true);
  };

  // Handle confirm clear all
  const handleConfirmClearAll = async () => {
    try {
      await clearAllDrafts();
      await loadData(); // Reload data
      setClearAllDialogOpen(false);
      showSnackbar('All drafts cleared successfully', 'success');
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to clear drafts',
        'error'
      );
    }
  };

  // Handle menu operations
  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    draftId: string
  ) => {
    setMenuAnchor(event.currentTarget);
    setMenuDraftId(draftId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDraftId(null);
  };

  // Show snackbar notification
  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Truncate text with ellipsis
  const truncateText = (text: string, maxLength: number): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Render loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadData}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Draft Documents
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and continue your saved document drafts
        </Typography>
      </Box>

      {/* Statistics */}
      {stats && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Statistics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {stats.totalDrafts}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Drafts
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="secondary">
                  {stats.totalMessages}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Messages
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="success.main">
                  {formatFileSize(stats.storageSize)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Storage Used
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<ClearAllIcon />}
                  onClick={handleClearAll}
                  disabled={stats.totalDrafts === 0}
                >
                  Clear All
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Empty state */}
      {drafts.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No drafts found
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Start a conversation in the chat to create your first draft
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/chat')}
            sx={{ mt: 2 }}
          >
            Start New Chat
          </Button>
        </Paper>
      ) : (
        /* Drafts list */
        <Grid container spacing={2}>
          {drafts.map((draft: DraftSummary) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              maxWidth={300}
              minWidth={300}
              key={draft.id}
            >
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1,
                    }}
                  >
                    <Tooltip title={draft.title} placement="top">
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{ flex: 1, mr: 1 }}
                      >
                        {truncateText(draft.title, 20)}
                      </Typography>
                    </Tooltip>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, draft.id)}
                    >
                      <MoreIcon />
                    </IconButton>
                  </Box>

                  {draft.description && (
                    <Tooltip title={draft.description} placement="top">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        {truncateText(draft.description, 40)}
                      </Typography>
                    </Tooltip>
                  )}

                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Document:</strong>{' '}
                    {truncateText(draft.documentTitle, 20)}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {truncateText(draft.preview, 60)}
                  </Typography>

                  <Box
                    sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
                  >
                    <Chip
                      icon={<MessageIcon />}
                      label={`${draft.messageCount} messages`}
                      size="small"
                      variant="outlined"
                    />
                    <Chip
                      icon={<ScheduleIcon />}
                      label={formatDistanceToNow(
                        new Date(draft?.updatedAt || ''),
                        {
                          addSuffix: true,
                        }
                      )}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </CardContent>

                <Divider />

                <CardActions>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<ContinueIcon />}
                    onClick={() => handleContinueDraft(draft.id)}
                    sx={{ mr: 1 }}
                  >
                    Continue
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleEditDraft(draft)}
                  >
                    Edit
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Floating Action Button for new chat */}
      {/* <Fab
        color="primary"
        aria-label="Start new chat"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => navigate('/chat')}
      >
        <AddIcon />
      </Fab> */}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (menuDraftId) handleDuplicateDraft(menuDraftId);
          }}
        >
          <DuplicateIcon sx={{ mr: 1 }} />
          Duplicate
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            const draft = drafts.find((d) => d.id === menuDraftId);
            if (draft) handleDeleteDraft(draft);
          }}
          sx={{ color: 'error.main' }}
        >
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Draft</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Draft</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{selectedDraft?.title}"? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Dialog */}
      <Dialog
        open={clearAllDialogOpen}
        onClose={() => setClearAllDialogOpen(false)}
      >
        <DialogTitle>Clear All Drafts</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all drafts? This action cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmClearAll}
            variant="contained"
            color="error"
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <SnackbarContent
          sx={{
            backgroundColor:
              snackbar.severity === 'success'
                ? 'success.main'
                : snackbar.severity === 'error'
                  ? 'error.main'
                  : 'info.main',
          }}
          message={snackbar.message}
        />
      </Snackbar>
    </Box>
  );
};

export default DraftsPage;
